import firebase from 'firebase/app';
import { useCallback, useEffect, useState } from 'react';

import { useCurrentUser } from '../currentUser';
import { callFirebaseFunction } from '../firebaseFunctions';

import { computeNewUxState, isTagOrg, type NewUxState } from './logic';

const SESSION_OFF_KEY_PREFIX = 'newux-session-off';

/**
 * Frontend deploy env. The browser has no GCLOUD_PROJECT, so we mirror the shared
 * getDeployEnv project-id mapping off the Firebase project the app connects to
 * (prod = `emplexp`). We resolve `isTag` on the frontend and pass it into the shared
 * pure brain, which stays env-agnostic so it is safe to bundle here.
 */
const feDeployEnv = (): 'prod' | 'dev' =>
  firebase.app().options.projectId === 'emplexp' ? 'prod' : 'dev';

/**
 * A stable id for the current login: changes on every real sign-in but is stable
 * across page reloads and new tabs. This is how the temporary "off" resets on a new
 * login only — never on a reload.
 */
const loginAnchor = (authUser: firebase.User): string =>
  authUser.metadata.lastSignInTime || '';

const storageKey = (uid: string) => `${SESSION_OFF_KEY_PREFIX}:${uid}`;

const readSessionOff = (uid: string, anchor: string): boolean => {
  try {
    const raw = localStorage.getItem(storageKey(uid));
    return !!raw && JSON.parse(raw)?.anchor === anchor;
  } catch {
    return false;
  }
};

const writeSessionOff = (uid: string, anchor: string, off: boolean): void => {
  try {
    if (off) localStorage.setItem(storageKey(uid), JSON.stringify({ anchor }));
    else localStorage.removeItem(storageKey(uid));
  } catch {
    // storage unavailable (private mode / blocked) — degrade to in-memory only
  }
};

/**
 * Session-scoped opt-out lives in a module-level store (like services/currentUser) so
 * EVERY useNewUx() consumer — the homepage gate, the banner, the user menu — re-renders
 * together when it flips. A per-component useState would only update the toggling
 * component, leaving the homepage on its stale value (the toggle would appear dead).
 * Keyed by uid+login-anchor; a new login re-initialises from localStorage (a stale
 * entry no longer matches the anchor and reads as off=false).
 */
let sessionOffState = false;
let sessionOffKey = ''; // `${uid}:${anchor}` the state corresponds to
const sessionOffListeners = new Set<() => void>();

const ensureSessionOffFor = (uid: string, anchor: string): void => {
  const key = `${uid}:${anchor}`;
  if (key !== sessionOffKey) {
    sessionOffKey = key;
    sessionOffState = readSessionOff(uid, anchor);
  }
};

/**
 * Optimistic commitment. Acknowledging is persisted by a backend write that only takes
 * effect on-screen once it round-trips back through the live user doc — so without this
 * the banner would linger (or never hide, if the write fails silently) after "Keep new
 * theme". We mirror the commit in this module store — same pattern as sessionOff — so the
 * banner hides the instant the user commits; the persisted `newUxAcknowledged` bit stays
 * the durable source of truth across logins. Keyed by uid so it never leaks to another
 * user; a real page reload clears it (falling back to the persisted bit).
 */
let committedUid: string | null = null;

const setSessionOffFor = (uid: string, anchor: string, off: boolean): void => {
  writeSessionOff(uid, anchor, off);
  sessionOffKey = `${uid}:${anchor}`;
  sessionOffState = off;
  sessionOffListeners.forEach((l) => l());
};

/**
 * createdAt is a Firestore Timestamp on the doc (a client Timestamp on the FE).
 * Duck-type the `.toMillis()` guard so we don't depend on an admin-typed guard here.
 */
const toMillisSafe = (value: unknown): number | undefined =>
  value && typeof (value as { toMillis?: unknown }).toMillis === 'function'
    ? (value as { toMillis: () => number }).toMillis()
    : undefined;

export interface UseNewUxResult extends NewUxState {
  /**
   * Shared action for BOTH the banner toggle and the user-menu toggle (identical
   * behavior). `true` commits to the new UX (persists, dismisses the banner forever);
   * `false` is a temporary opt-out for this login only (resets to on next login).
   */
  setEnabled: (on: boolean) => void;
}

/**
 * The single frontend entry point for NEW_UX. Every overhaul surface — the homepage
 * now; the timeline and timeline-item modals in later PRs — gates on `enabled` from
 * here. Effective-value logic is mirrored in ./logic (from @dadohr/shared/services/newUx).
 */
export const useNewUx = (): UseNewUxResult => {
  const currentUser = useCurrentUser();
  const uid = currentUser.id;
  const anchor = loginAnchor(currentUser.authUser);

  // Idempotent: only re-reads localStorage when the user/login actually changes.
  ensureSessionOffFor(uid, anchor);

  // Subscribe to the shared store so a toggle anywhere re-renders this consumer.
  const [, forceRender] = useState(0);
  useEffect(() => {
    const listener = () => forceRender((n) => n + 1);
    sessionOffListeners.add(listener);
    // Re-sync in case the store changed between render and effect (e.g. login switch).
    listener();
    return () => {
      sessionOffListeners.delete(listener);
    };
  }, [uid, anchor]);

  // Also react to changes from other tabs (localStorage 'storage' events).
  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key === storageKey(uid)) {
        sessionOffState = readSessionOff(uid, anchor);
        sessionOffListeners.forEach((l) => l());
      }
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, [uid, anchor]);

  const state = computeNewUxState({
    acknowledged: currentUser.newUxAcknowledged === true || committedUid === uid,
    createdAtMs: toMillisSafe(currentUser.createdAt),
    isTag: isTagOrg(currentUser.org?.id, feDeployEnv()),
    sessionOff: sessionOffState
  });

  const setEnabled = useCallback(
    (on: boolean) => {
      if (on) {
        if (!currentUser.newUxAcknowledged) {
          // Commit optimistically so the banner hides immediately, then persist. The
          // live currentUser listener confirms `acknowledged` when the write lands; a
          // failed write surfaces in the console rather than being swallowed.
          committedUid = uid;
          callFirebaseFunction('adminUser-setNewUxAcknowledged').catch((err) => {
            // eslint-disable-next-line no-console
            console.error('Failed to persist NEW_UX acknowledgement', err);
          });
        }
        // Notifies every useNewUx() consumer (incl. the optimistic commit above).
        setSessionOffFor(uid, anchor, false);
      } else {
        setSessionOffFor(uid, anchor, true);
      }
    },
    [uid, anchor, currentUser.newUxAcknowledged]
  );

  return { ...state, setEnabled };
};
